import { Button } from '@heidi/design-system/button';
import { Textarea } from '@heidi/design-system/textarea';
import { TypographyCaption } from '@heidi/design-system/typography';
import { zodResolver } from '@hookform/resolvers/zod';
import { Flag, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import {
  parseReportIssueDraftInput,
  type ReportIssueType,
} from '../../contracts/report-issue.types';
import type { ResolveDraftApproval } from './draft-approval-block';
import { InlineApprovalActions } from './inline-approval-actions';
import { reportIssueApprovalMessages as messages } from './report-issue-approval-block.messages';

const DESCRIPTION_MAX_LENGTH = 4000;
const COUNTER_VISIBLE_FROM = DESCRIPTION_MAX_LENGTH - 200;

const reportIssueFormSchema = z.object({
  description: z.string().trim().min(1).max(DESCRIPTION_MAX_LENGTH),
});

type ReportIssueFormValues = z.infer<typeof reportIssueFormSchema>;

export interface ReportIssueApprovalBlockProps {
  toolInput: Record<string, unknown>;
  approvalId?: string;
  onResolve?: ResolveDraftApproval;
  /** Analytics port: fired when the user submits or cancels the draft. */
  onResolved?: (
    issueType: ReportIssueType,
    outcome: 'granted' | 'denied',
    extra: { description_edited: boolean }
  ) => void;
}

/**
 * Report card for the `report_issue` tool's approval pause. The card shows the
 * agent-drafted bug description for the user to review and edit. Nothing
 * leaves Heidi until the user submits; Cancel denies the approval and the
 * agent drops the report.
 *
 * The tool no longer files clinical safety reports: those go through the SOP
 * routes (the note's thumbs down with Safety issue ticked, or support). A
 * safety card parked before that change names those routes and can only be
 * dismissed, so clinical content is never sent as a bug.
 */
export const ReportIssueApprovalBlock = ({
  toolInput,
  approvalId,
  onResolve,
  onResolved,
}: ReportIssueApprovalBlockProps) => {
  const intl = useIntl();
  const draft = parseReportIssueDraftInput(toolInput);
  const isSafety = draft.issueType === 'safety';
  const initialDescription = draft.description;
  const [pendingVerdict, setPendingVerdict] = useState<'approve' | 'deny' | null>(null);
  const [submitError, setSubmitError] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ReportIssueFormValues>({
    resolver: zodResolver(reportIssueFormSchema),
    defaultValues: { description: initialDescription },
  });
  const descriptionValue = watch('description') ?? '';

  const canAct = Boolean(approvalId) && Boolean(onResolve);
  const isBusy = pendingVerdict !== null;

  const resolveWith = (
    verdict: 'approve' | 'deny',
    modifiedInput?: Record<string, unknown> | null
  ): void => {
    if (!approvalId || !onResolve || isBusy) return;
    setSubmitError(false);
    setPendingVerdict(verdict);
    void onResolve(approvalId, verdict, undefined, modifiedInput ?? null)
      .then(() => {
        onResolved?.(draft.issueType, verdict === 'approve' ? 'granted' : 'denied', {
          description_edited: descriptionValue.trim() !== initialDescription.trim(),
        });
      })
      .catch(() => {
        setPendingVerdict(null);
        setSubmitError(true);
      });
  };

  const submit = handleSubmit((values) => {
    const text = values.description.trim();
    resolveWith('approve', {
      issue_type: draft.issueType,
      title: draft.title,
      description: text,
    });
  });

  if (isSafety) {
    return (
      <div
        className="my-4 rounded-lg border border-border bg-surface-accent px-4 py-3 first:mt-0"
        data-testid="report-issue-approval-block"
      >
        <div className="mb-1 flex items-center gap-1.5">
          <Flag className="size-3.5 text-text-tertiary" />
          <TypographyCaption className="text-text-primary">
            {intl.formatMessage(messages.headerSafety)}
          </TypographyCaption>
        </div>
        <TypographyCaption className="block text-pretty text-text-secondary">
          {intl.formatMessage(messages.safetyRoutes)}
        </TypographyCaption>
        {canAct ? (
          <div className="mt-4 flex flex-col items-end gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => resolveWith('deny')}
              disabled={isBusy}
              data-testid="report-issue-cancel"
            >
              {pendingVerdict === 'deny' && <Loader2 className="size-3.5 animate-spin" />}
              {intl.formatMessage(messages.dismissSafety)}
            </Button>
            {submitError ? (
              <TypographyCaption
                className="text-validation-destructive"
                data-testid="report-issue-submit-error"
              >
                {intl.formatMessage(messages.submitError)}
              </TypographyCaption>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="my-4 rounded-lg border border-border bg-surface-accent px-4 py-3 first:mt-0"
      data-testid="report-issue-approval-block"
    >
      <div className="mb-1 flex items-center gap-1.5">
        <Flag className="size-3.5 text-text-tertiary" />
        <TypographyCaption className="text-text-primary">
          {intl.formatMessage(messages.headerBug)}
        </TypographyCaption>
      </div>
      <TypographyCaption className="mb-3 block text-text-secondary">
        {intl.formatMessage(messages.reviewBug)}
      </TypographyCaption>

      <Textarea
        {...register('description')}
        rows={5}
        maxLength={DESCRIPTION_MAX_LENGTH}
        aria-label={intl.formatMessage(messages.descriptionLabel)}
        disabled={isBusy}
        className="resize-y"
        data-testid="report-issue-description"
      />
      {errors.description ? (
        <TypographyCaption className="mt-1 block text-validation-destructive">
          {intl.formatMessage(messages.descriptionRequired)}
        </TypographyCaption>
      ) : null}
      {descriptionValue.length >= COUNTER_VISIBLE_FROM ? (
        <TypographyCaption className="mt-1 block text-end tabular-nums text-text-tertiary">
          {intl.formatMessage(messages.characterCount, {
            count: descriptionValue.length,
            max: DESCRIPTION_MAX_LENGTH,
          })}
        </TypographyCaption>
      ) : null}
      <TypographyCaption className="mt-2 block text-text-tertiary">
        {intl.formatMessage(messages.attachedDetails)}
      </TypographyCaption>

      {canAct ? (
        <div className="mt-4 flex flex-col items-end gap-1">
          <InlineApprovalActions
            onApprove={() => void submit()}
            onDeny={() => resolveWith('deny')}
            pendingVerdict={pendingVerdict}
            approveLabel={intl.formatMessage(messages.submitBug)}
            approveTestId="report-issue-submit"
            denyTestId="report-issue-cancel"
          />
          {submitError ? (
            <TypographyCaption
              className="text-validation-destructive"
              data-testid="report-issue-submit-error"
            >
              {intl.formatMessage(messages.submitError)}
            </TypographyCaption>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
